<template>
<div class="chapter">
<div class="inner_page">
<h3 class="calibre14" id="PART_I_CHAPTER_IV">CHAPTER IV.<br class="calibre9"/>
<span class="smaller">THE INNER POLAR SEA.</span></h3>
<p class="calibre3">1. The Arctic Sea, in some of its features, forcibly impresses
us with its resemblance to the glaciers of the Alps. In both
cases, the ice presses from a region, colder and less favoured
by climate, towards one warmer and more favoured. In the
Alpine glaciers, the movement is from above downwards;
in the Frozen Ocean, the movement is from a higher to a
lower geographical latitude. In both cases, the tongues and
spurs of the masses of ice formed by the configuration of the
land or by currents of the sea, terminate, whenever they reach
an isothermal curve of altitude or latitude, the mean temperature
of which suffices to dissolve them or prevent their
formation. Moraines also have their equivalent in the Arctic
Sea; for it is an established fact that icebergs and ice-fields
laden with the <i class="calibre10">débris</i> and rubbish of Arctic lands, deposit
these burdens round the outer edge of the Frozen Ocean,
and to this process, partially at least, the origin of the Newfoundland
Banks is ascribed. If this comparison between the
phenomena of high latitudes and great altitudes be just, then
we should have as much reason to believe in the existence of
the so-called open Polar Sea, as we should have to maintain,
that in our glacier ranges ice ceases to be formed above a
certain altitude.</p>
<p class="calibre3">2. The belief of past times<a class="reference" id="FNanchor_11"></a><span class="fnanchor" href="#/Text/Section0043#Footnote_11" title="Link target outside of Sample">[11]</span> in such a sea shows how
unsatisfactory is the simple to man’s mind, and how old is
his tendency to clothe the remote and the uncommon with<span id="pgepubid00054" title="[26]"><a class="reference" id="Page_26" title="[26]"></a></span>
a garment of the marvellous. What was the open Polar Sea
but the “Harz Sea” of the North, or the legendary zone
of the ever-sunny Eden of the Hyperboreans, far beyond
the land of the Anthropophagi over which was spread an
atmosphere veiled in snow, and through which no light could
penetrate! Who has ever seen this open Polar Sea? Do
the accounts of navigators confirm its existence? Nay—their
accounts are rather a series of counter-statements:
Hudson, Baffin, Phipps, Tschitschagoff, Buchan, Franklin,
Parry, Collinson, Scoresby, M’Clintock, Koldewey, Torell,
Nordenskjöld, have all expressed their disbelief in its existence.
If some have pretended that they have seen it, how
strange it is that they never sailed on it! It has recently
been attempted to make the great champion of the Polar
question, Dr. Petermann, a supporter of this conception; but
in the “Mittheilungen” of this highly meritorious geographer,
there are many passages which most emphatically protest
against it. His views extend only to an inner Polar Sea
navigable under certain circumstances, and every one acquainted
with those regions may adopt his point of view,
though he refuses to admit the existence of the open
Polar Sea.</p>
<p class="calibre3">3. In those centuries when the Natural Sciences were
little cultivated, when the theories of the Trade Winds, of
Equatorial and Polar sea-currents, were still unknown, and
when as yet the processes in the Frozen Ocean had not
been submitted to scientific investigation, we cannot be surprised
at the preconceptions which were formed concerning
its phenomena. In those times all beyond Norway was a
chaos of ice-filled darkness; the necessity of a scientific
investigation of those wastes was not felt; and down to the
time of Sir John Ross, Polar navigators on their return home
brought with them no kind of scientific knowledge of Nature
in the Arctic regions. To reach India was the main if
not the only end they had in view. The instructions which
Willoughby, the first Polar navigator, received, give us an
insight into the delusions of earlier times. These, for example,
warned adventurers against men-eaters who swam naked in the
sea, and in the rivers. It was the period of fables long since
forgotten. Maldonado, de Fuca, Bernarda, Yelmer, Andrejew,<span id="pgepubid00055" title="[27]"><a class="reference" id="Page_27" title="[27]"></a></span>
Martinière, and the whale-fishers, brought home tales of passages
to India discovered, of new continents, of the ascertained
connexion of Novaya Zemlya with the northernmost point of
Siberia (Yelmerland) or even with Greenland. Two centuries
ago the failure of all attempts at a North-East passage was
attributed to Russia’s commercial policy, inasmuch as it had
been proved to the satisfaction of all, that the heat was
greater in the north, that the seas there ceased to freeze, and
that the country was covered with a luxuriant green!</p>
<p class="calibre3">4. There was, indeed, a certain logical consequence in the
belief of an inner Polar Sea, as long as it was unknown that
ice is formed on the open sea as well as on the coasts. There
was also one argument, which made the existence of such a
sea not altogether improbable. It might be assumed, that
the formation of ice renewed every year in the Arctic
regions, would necessarily produce eternal bulwarks of congelation
and destroy all organic life, unless sea-currents
modified these extremes of climate. The ice which is formed
round the Pole—it was argued—is not of an unlimited but
of a definite quantity. Since, then, this quantity of ice must
be brought with tolerable uniformity from the innermost Polar
region to lower latitudes by the action of sea-currents, there
are at least one or two months of the summer when the ice
is at a minimum, when no new formation takes place, and
when a sea relatively ice-free may appear in the place of the
sea which had been covered with ice. This sea would be the
more open and navigable, just in proportion as less land
might be found at the Pole. But in this assumption it is
implied, that the ice moves with perfect regularity and in
radial lines from a given point without any disturbance from
winds, or counter-currents, or land, consequently with a quiet
simplicity of hydrography, for which Nature, neither there
nor elsewhere, shows any predilection. Dove makes the
mean annual temperature of the North Pole, 2·5° F.; but
it is probably still less. What, then, is the probability of an
open Polar Sea, if this annual mean only be considered? All
the accounts too of animal life increasing in exuberance as we
advance northwards—from which a more favourable climate
within the innermost Polar region and an open Polar Sea
have been inferred—must be received with caution, for the<span id="pgepubid00056" title="[28]"><a class="reference" id="Page_28" title="[28]"></a></span>
appearance of numerous flocks of birds proves only that they
remain where open water prevails for a time and that they
change their abode with its change of place.</p>
<p class="calibre3">5. In more recent times great influence has been attributed
to the Gulf Stream as a power influencing all the seas, known
and unknown, of the whole Arctic region. Dr. Petermann,
however, in a lately published work, endeavours to show that
its effects are discernible only on the northern seas of Spitzbergen
and Novaya Zemlya. Its action on the coasts of Spitzbergen
has been indisputably established by the Swedes, who
discovered there certain tropical plants (<i class="calibre10">Entada gigalobium</i>);
but the penetration of the warmer waters of this current to
the northern coasts of Novaya Zemlya has not been so positively
ascertained. In the Austrian Expedition of 1873-4, we
discovered no proofs of its existence. We found neither the
constant current, nor the water of a higher temperature, which
characterizes that renowned stream.</p>
<p class="calibre3">6. For a long time the “ice-holes,” seen by Wrangel and
Morton, were regarded as indications of an ice-free Polar Sea.
With regard to those seen by Morton in 81° 22′, Richardson
very justly remarks: “The open water of the Kennedy Channel
is not of greater extent in the month of June than the open
spaces which have occasionally been seen in summer on the
north of Spitzbergen by whale-fishers.” Wrangel, when he
describes the “Polynjii,” which he saw on the east of the New
Siberian Islands, accounts for them by the action of a local
coast-wind; and yet Wrangel would have been the first to
favour the notion of an inner Polar Sea, for he still thought,
in opposition to Scoresby, that ice could not be formed on the
open sea, because of the absence of land as a support for the
ice in its formation.</p>
<p class="calibre3">7. The first practical application of the theory of an open
Polar Sea was long ago devised by Plancius; the discovery,
namely, of a route in high latitudes to China. But the
expeditions to the North Pole, properly so termed, sprang
also from this theory, which was held with the greatest
pertinacity. The evidence of unsuccessful undertakings was
always met and outweighed by the counter-experience of one
favourable year in the ice. Thus Barentz, in the exceedingly
propitious summer of 1594, advanced without difficulty one<span id="pgepubid00057" title="[29]"><a class="reference" id="Page_29" title="[29]"></a></span>
degree of latitude beyond the northern extremity of Novaya
Zemlya, while his successors frequently encountered insurmountable
difficulties at Cape Nassau, and he himself in the
following year, 1595, found the state of the ice changed much
for the worse. In the years 1871, 1874, Mack, Carlsen, and
the two Austro-Hungarian expeditions came upon an open
sea in the very places where very few, if any, water-ways were
to be seen in 1872 and 1873. In the summers of 1816, 1817,
the mighty stream of ice on the coast of East Greenland had
decreased to such an extent that Scoresby met with little
ice between 74° and 80° N.L., but since then whalers have
constantly seen the heaviest ice there, heavier than anywhere
else. In 1753 and 1754, the Sea of Kara and the Novaya
Zemlya Sea were free from ice. But in subsequent years the
whale-fishers knocked in vain at their ice-barred entrances.
In 1823 Lütke from a point on the west coast of the Sea
of Kara saw that sea without ice; but, in the middle of
August, 1833, Pachtussow found the western side of that sea
open, while in the previous year he himself could not pass the
Karian Gates. Again in 1743 and 1773, the North Spitzbergen
Sea held out promises the most inviting, which might
possibly have permitted the reaching of a still higher degree
of latitude than that which Nordenskjöld and Koldewey
attained in 1868. Sir John Ross, in the first year of his
second voyage, found all things most favourable for navigation;
but in the following year the very reverse; and
Sir James C. Ross experienced the same alternation of circumstances
in the Southern Polar Sea. In 1850, Penny
found the Wellington Channel free from ice, but in 1852,
Belcher, although he penetrated far further than Penny,
was confronted in the same channel by pack- and drift-ice.
Scoresby the younger, to whose profound faculty of observation
we owe the most significant hints on the nature of
the Polar Sea, although he had navigated the Greenland
ice-ocean for twenty years, landed only once on its coast.
The Swedish expedition (1861) could approach the north-east
of Spitzbergen only in boats; Smith sailed over the
sea there (1871) as far as Cape Smith. The walrus-hunter,
Matilas, sailed round (1864) the north-east island completely,
and Carlsen, an ice navigator, as successful as he was skilful, in<span title="[30]"><a class="reference" id="Page_30" title="[30]"></a></span>
1863 circumnavigated Spitzbergen, and in 1871 Novaya Zemlya,
and discovered there the relics of Barentz’s winter quarters. In
1872, King Karl Land was circumnavigated, although both
Koldewey and Nordenskjöld (1868) as well as the first Austrian
expedition (1871) had in vain attempted to approach it.
How greatly also, in the same year, the state of the ice varies
in different places, is proved by the fact, that Franklin learnt
from the whalers that they never saw the ice so thick and so
strong in Davis Straits as at the end of July 1819, while
Parry, more to the north by some degrees of latitude, pursued
his path of discovery even to Melville Island, and in the following
year returned to England without meeting any special
obstacles. These examples, to which many more might be
added, show how variable are the chances of ice-navigation
from one year to another. But however variable the conditions
of the ice may be, the impediments, even under the
most favourable circumstances, are so very great, that we
have never been able to penetrate the innermost Polar
regions,—<i class="calibre10">penetrate</i>, that is, <i class="calibre10">to where, according to the views
of an earlier time, the open Polar Sea should be found</i>.</p>
<p class="calibre3">8. Those propitious ice-years amount therefore to nothing
more than a greater recession of the outer ice-barrier—trifling
when compared with the mighty whole—or to an
increased navigability of certain coast-waters, or to a local
loosening of the inner Polar ice-net. In reality the whole
Arctic Sea, with its countless ice-fields and floes, and its web
of fine interlacing water-ways, is nothing but a net constantly
in motion from local, terrestrial, or cosmical causes. All the
changes and phenomena of this mighty network lead us to
infer the existence of frozen seas up to the Pole itself; and
according to my own experience, gained in three expeditions,
I consider <i class="calibre10">that the states of the ice between 82° and 90°</i> N.L.
<i class="calibre10">will not essentially differ from those which have been observed
south of latitude 82°; I incline rather to the belief that they will
be found worse instead of better</i>.</p>
<p class="calibre3">9. If this view be correct, it will remain an insuperable
difficulty to reach the Pole with a ship. The penetrating
to 82° or 83° exhausts, according to all past experience, the
disposable time for navigation, and presupposes moreover the
most favourable conditions for the attaining of such high<span id="pgepubid00058" title="[31]"><a class="reference" id="Page_31" title="[31]"></a></span>
latitudes. A ship which reaches 82° N.L. by the beginning
of autumn must risk nothing more, should only navigate
really open water, and the expediency of securing a winter
harbour should then outweigh every other consideration.</p>
<p class="calibre3">10. He who expects with a ship of the present construction
to reach the Pole in a single summer, necessarily believes in
an ocean at the Pole. But even if an expedition should
penetrate to 84° in Smith’s Sound, or should reach Cape
Tcheljuskin on the north-east route, it would not follow that
such an ocean exists, but only that the Polar Sea presents at
different times and in different places open water-ways, which
may enable a ship to advance beyond a point hitherto reached;
but it is improbable that the circumstances which favoured
this will be repeated the next summer, so as to permit the
ships to penetrate still further—or to return. The last
American expedition returned without being able to speak
decisively as to the possibility of navigating Lincoln Sea, and
since this has not yet been verified by fact, we must suspend
our judgment on the matter. To the English expedition,
which has taken this route to the Pole, is reserved the great
work of throwing light on the region of Upper Smith’s
Sound, and the whole civilized world will hail with joy any
successes which a nation, so long conspicuous for its perseverance
in the cause of discovery, may happily achieve.</p>
<p class="calibre3"><span id="pgepubid00059" title="[32]"><a class="reference" id="Page_32" title="[32]"></a></span></p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0010.vue?55083a26